// Marketing Director — approval queue (all channels) + SMS tap-to-send queue.
// One human decision per message; deliberately no bulk actions anywhere.
import { useCallback, useEffect, useState } from "react";
import { marketingFetch } from "../lib/api";

const CHANNEL_BADGE = {
  email: "Email",
  sms_p2p: "SMS",
  linkedin_draft: "LinkedIn",
  x_draft: "X",
  facebook_draft: "Facebook",
  instagram_draft: "Instagram",
  call_note: "Call note",
};

function DraftCard({ item, facilityId, onDone }) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(item.payload?.body || "");
  const [error, setError] = useState(null);
  const social = item.channel.endsWith("_draft");

  const call = async (path, payload = {}) => {
    const r = await marketingFetch(path, {
      method: "POST",
      body: JSON.stringify({ facility_id: facilityId, ...payload }),
    });
    if (!r.ok) {
      const d = r.data?.detail;
      setError(
        typeof d === "string" ? d : JSON.stringify(d) || "Action failed",
      );
      return false;
    }
    onDone();
    return true;
  };

  const approve = async () => {
    if (editing && body !== item.payload?.body) {
      const ok = await call(`/touches/${item.id}/edit`, { body });
      if (!ok) return;
    }
    await call(`/touches/${item.id}/approve`);
  };

  return (
    <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2">
      {error && <div className="text-sm text-red-700">{error}</div>}
      <div className="flex justify-between items-center">
        <div className="text-sm">
          <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-medium mr-2">
            {CHANNEL_BADGE[item.channel] || item.channel}
          </span>
          {item.prospect_id ? (
            <span className="text-slate-700">
              {item.display_name || "Unnamed"} · {item.org_name || ""}
              <span className="text-slate-400">
                {" "}
                · step {item.sequence_step}
              </span>
            </span>
          ) : (
            <span className="text-slate-500">Campaign-level post</span>
          )}
        </div>
        <button
          onClick={() => setEditing((v) => !v)}
          className="text-xs text-slate-500 underline"
        >
          {editing ? "cancel edit" : "edit"}
        </button>
      </div>
      {editing ? (
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
        />
      ) : (
        <pre className="text-sm text-slate-600 whitespace-pre-wrap font-sans">
          {item.payload?.body}
        </pre>
      )}
      <div className="flex gap-2">
        <button
          onClick={approve}
          className="px-3 py-1.5 rounded-lg bg-red-700 text-white text-sm hover:bg-red-800"
        >
          {item.channel === "email"
            ? "Approve & queue send"
            : item.channel === "sms_p2p"
              ? "Approve to SMS queue"
              : "Approve for manual post"}
        </button>
        <button
          onClick={() => {
            const reason = window.prompt("Reason for rejecting?") || "";
            call(`/touches/${item.id}/reject`, { reason });
          }}
          className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-sm"
        >
          Reject
        </button>
        {social && (
          <span className="text-xs text-slate-400 self-center">
            After approval: copy the text, post it on the network, then mark
            posted below.
          </span>
        )}
      </div>
    </div>
  );
}

function SmsQueue({ facilityId }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [replySummary, setReplySummary] = useState("");
  const [lastSent, setLastSent] = useState(null);
  const [tick, setTick] = useState(0);

  const load = useCallback(() => {
    marketingFetch(`/sms-queue?facility_id=${facilityId}`).then(
      (r) => r.ok && setState(r.data),
    );
  }, [facilityId]);
  useEffect(load, [load, tick]);
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 5000);
    return () => clearInterval(t);
  }, []);

  const act = async (touchId, path, payload = {}) => {
    setError(null);
    const r = await marketingFetch(`/touches/${touchId}/${path}`, {
      method: "POST",
      body: JSON.stringify({ facility_id: facilityId, ...payload }),
    });
    if (!r.ok) {
      const d = r.data?.detail;
      setError(typeof d === "string" ? d : "Action failed");
      return false;
    }
    load();
    return true;
  };

  if (!state) return <div className="text-slate-400 text-sm">Loading…</div>;
  const { item, pacing } = state;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <div className="space-y-3">
      <div className="text-sm text-slate-600">
        Sent today: <b>{pacing.sent_today}</b>
        {pacing.daily_cap != null && <> / {pacing.daily_cap}</>}
        {pacing.gap_remaining_seconds > 0 && (
          <span className="ml-3 text-slate-500">
            Pacing: next send unlocks in {pacing.gap_remaining_seconds}s —
            spacing protects your number from carrier filtering.
          </span>
        )}
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      {pacing.cap_reached ? (
        <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 text-slate-600 text-sm">
          Daily cap reached — the queue reopens tomorrow. One message at a time,
          by design.
        </div>
      ) : !item ? (
        <div className="p-4 rounded-xl border border-slate-200 bg-white text-slate-400 text-sm">
          SMS queue is empty.
        </div>
      ) : (
        <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-3">
          <div className="text-sm text-slate-700 font-medium">
            To: {item.display_name}
          </div>
          <pre className="text-sm text-slate-600 whitespace-pre-wrap font-sans">
            {item.body}
          </pre>
          <div className="flex flex-wrap gap-2">
            <a
              href={isIOS ? item.deep_links.ios : item.deep_links.android}
              className={`px-4 py-2 rounded-lg text-sm text-white ${
                pacing.gap_remaining_seconds > 0
                  ? "bg-slate-300 pointer-events-none"
                  : "bg-red-700 hover:bg-red-800"
              }`}
            >
              Open in Messages
            </a>
            <button
              onClick={async () => {
                if (await act(item.touch_id, "mark-posted"))
                  setLastSent(item.touch_id);
              }}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-700"
            >
              I sent it
            </button>
            <button
              onClick={() =>
                act(item.touch_id, "reject", { reason: "skipped in SMS queue" })
              }
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-500"
            >
              Skip
            </button>
          </div>
        </div>
      )}
      {lastSent && (
        <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2">
          <div className="text-sm font-medium text-slate-700">
            Log the reply (optional — required for STOP)
          </div>
          <button
            onClick={() =>
              act(lastSent, "log-reply", {
                tag: "opt_out",
                summary: "STOP",
              }).then((ok) => ok && setLastSent(null))
            }
            className="px-4 py-2 rounded-lg bg-red-700 text-white text-sm"
          >
            They opted out (STOP) — suppress now
          </button>
          <div className="flex gap-2">
            <input
              value={replySummary}
              onChange={(e) => setReplySummary(e.target.value)}
              placeholder="Summarize their reply"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm"
            />
            {["positive", "neutral", "negative"].map((tag) => (
              <button
                key={tag}
                onClick={() =>
                  act(lastSent, "log-reply", {
                    tag,
                    summary: replySummary,
                  }).then(
                    (ok) => ok && (setLastSent(null), setReplySummary("")),
                  )
                }
                className="px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-600"
              >
                {tag}
              </button>
            ))}
          </div>
          <button
            onClick={() => setLastSent(null)}
            className="text-xs text-slate-400 underline"
          >
            no reply to log
          </button>
        </div>
      )}
    </div>
  );
}

export function MarketingApprovals({ facilityId }) {
  const [queue, setQueue] = useState([]);
  const [sub, setSub] = useState("drafts");
  const load = useCallback(() => {
    marketingFetch(`/approval-queue?facility_id=${facilityId}`).then(
      (r) => r.ok && setQueue(r.data.queue || []),
    );
  }, [facilityId]);
  useEffect(load, [load]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {[
          ["drafts", `Drafts (${queue.length})`],
          ["sms", "SMS tap-to-send"],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setSub(k)}
            className={`px-3 py-1.5 rounded-lg text-sm ${
              sub === k
                ? "bg-red-700 text-white"
                : "border border-slate-300 text-slate-600"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {sub === "drafts" && (
        <div className="space-y-3">
          {queue.map((item) => (
            <DraftCard
              key={item.id}
              item={item}
              facilityId={facilityId}
              onDone={load}
            />
          ))}
          {!queue.length && (
            <div className="p-6 text-slate-400 text-sm">
              Nothing awaiting approval.
            </div>
          )}
        </div>
      )}
      {sub === "sms" && <SmsQueue facilityId={facilityId} />}
    </div>
  );
}
